import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { CheckCircle, Circle } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { useI18n } from "@/context/I18nContext";
import { countries } from "@/data/countries";
import { cachedFetch } from "@/utils/cacheManager";
import useAdaptivePolling from "@/hooks/useAdaptivePolling";
import useTimerWS from "@/hooks/useTimerWS";
import useNetworkStatus from "@/hooks/useNetworkStatus";
import { TimerActiveDisplay, isTimerLayoutActive } from "@/components/timer/TimerActiveDisplay";
import { useEvent } from "@/components/EventLayout";
import useSimulationMode from "@/hooks/useSimulationMode";
import { ss, offsetTransform, offsetTransformMirrored } from "@/utils/displayStyle";
import { requestFullscreen, exitFullscreen, getFullscreenElement, onFullscreenChange } from "@/utils/fullscreen";
import { sortJudgeNumbers } from "@/utils/judgeOrder";
import ReloadButton from "@/components/ReloadButton";
import usePwaManifest from "@/hooks/usePwaManifest";

import { BACKEND_URL } from '../utils/apiBase';
const API = `${BACKEND_URL}/api`;

// Helper function to build correct image URL - FULL QUALITY for Display
const getImageUrl = (url, options = {}) => {
  if (!url) return '';
  
  // For Display page: Keep maximum quality, only convert format for bandwidth
  const { width = null, quality = 100, format = 'auto' } = options;
  
  // If it's a Cloudinary URL, add minimal optimization (only format conversion)
  if (url.startsWith('https://res.cloudinary.com/') || url.startsWith('http://res.cloudinary.com/')) {
    // Parse Cloudinary URL and inject optimization parameters
    const urlParts = url.split('/upload/');
    if (urlParts.length === 2) {
      // Only convert to WebP (lighter) but keep full quality and dimensions
      const transformations = `f_${format},q_${quality}`;
      return `${urlParts[0]}/upload/${transformations}/${urlParts[1]}`;
    }
    return url; // Return as-is if can't parse
  }
  
  // If it's a relative path, prepend BACKEND_URL
  if (url.startsWith('/api/uploads/')) {
    return `${BACKEND_URL}${url}`;
  }
  
  // Default: treat as relative path
  return `${BACKEND_URL}${url}`;
};

const DisplayPage = () => {
  usePwaManifest('/manifest-display.json');
  const { theme } = useTheme();
  const { isOnline } = useNetworkStatus();
  const { t } = useI18n();
  const event = useEvent();
  const { battleTimers, circleTimers, settings: countdownSettings, refreshBattle, refreshCircle } = useTimerWS(event?.event_id);
  const countdownMode = countdownSettings?.countdown_mode === true;
  const { simulationMode } = useSimulationMode(event?.event_id);
  const countdownSoundEnabled = countdownSettings?.countdown_sound_enabled === true;

  // Refs to access latest timers in async callbacks (avoid stale closures)
  const battleTimersRef = useRef(battleTimers);
  const circleTimersRef = useRef(circleTimers);
  useEffect(() => { battleTimersRef.current = battleTimers; }, [battleTimers]);
  useEffect(() => { circleTimersRef.current = circleTimers; }, [circleTimers]);
  const [currentBattle, setCurrentBattle] = useState(null);
  const [currentCircle, setCurrentCircle] = useState(null);
  const [revealedVotes, setRevealedVotes] = useState([]);
  const [judges, setJudges] = useState([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [tournaments, setTournaments] = useState([]);
  const previousBattleIdRef = useRef(null);
  const previousCircleIdRef = useRef(null);

  // ===== Display auto-scale (fit-to-screen) =====
  // The Display renders inside a fixed "design" canvas (default 1920x1080).
  // A CSS transform scales the canvas so its content ALWAYS fits within
  // any screen resolution (1792x768, 1366x768, ultrawide, vertical TV, etc.).
  // Override the design canvas via URL query (?w=1792&h=768) — useful for
  // event-specific screens that have an unusual aspect ratio.
  const urlParams = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;
  const designWidth = parseInt(
    urlParams?.get("w") || theme.display_design_width || 1920,
    10,
  ) || 1920;
  const designHeight = parseInt(
    urlParams?.get("h") || theme.display_design_height || 1080,
    10,
  ) || 1080;
  // Height factor: 1.0 for standard 16:9, <1 when canvas is shorter (e.g. 1792x768 → ~0.76).
  // Used to proportionally shrink height-sensitive elements (photos, fonts, paddings)
  // so the full circle reveal panel fits without overflow on short displays.
  const heightFactor = Math.min(1, designHeight / (designWidth * 9 / 16));
  const [displayFit, setDisplayFit] = useState({ scale: 1, offsetX: 0, offsetY: 0 });
  useEffect(() => {
    const compute = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      const s = Math.min(w / designWidth, h / designHeight);
      setDisplayFit({
        scale: s,
        offsetX: Math.max(0, (w - designWidth * s) / 2),
        offsetY: Math.max(0, (h - designHeight * s) / 2),
      });
    };
    compute();
    window.addEventListener("resize", compute);
    window.addEventListener("orientationchange", compute);
    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("orientationchange", compute);
    };
  }, [designWidth, designHeight]);
  
  // Refs to track previous data fingerprints (avoids stale closures + unnecessary re-renders)
  const dataFingerprintRef = useRef({ battleId: null, battleVotes: 0, battleRevealed: 0, circleId: null, circleVotes: 0, circleRevealed: 0 });
  // Grace timers: when the API stops returning a battle/circle we owned, we
  // wait 2 s before actually clearing the state. This eliminates every
  // sub-second "En attente de la prochaine battle..." flicker on the Big
  // Screen (between two battles, after a reset, on a transient race, etc.).
  // If the next poll returns a battle/circle again, the pending clear is
  // cancelled and the display never blinks.
  const battleClearGraceRef = useRef(null);
  const circleClearGraceRef = useRef(null);
  const NULL_CLEAR_GRACE_MS = 2000;
  
  // Couleurs distinctes pour chaque athlète
  const athlete1Color = ss(theme, 'battle_vote', 'athlete1_color', { color: theme.display_score_color }).color || '#a78bfa';
  const athlete2Color = ss(theme, 'battle_vote', 'athlete2_color', { color: theme.display_secondary_score }).color || '#ec4899';
  const winnerHighlightColor = ss(theme, 'battle_vote', 'winner_highlight', { color: theme.winner_highlight_color }).color || '#22c55e';
  
  // Map round names to display text
  const getRoundDisplayName = (round) => {
    const roundNames = {
      'TOP32': t('rounds.top32'),
      'TOP16': t('rounds.top16'),
      'QUARTER_FINALS': t('rounds.quarterFinals'),
      'SEMI_FINALS': t('rounds.semiFinals'),
      'THIRD_PLACE': t('rounds.thirdPlace'),
      'FINAL': t('rounds.final')
    };
    return roundNames[round] || round;
  };
  
  // Helper function to get country code from country name
  const getCountryCode = (countryName) => {
    if (!countryName) return null;
    const country = countries.find(c => 
      c.name.toLowerCase() === countryName.toLowerCase()
    );
    return country ? country.code : null;
  };
  
  // Component for round flag - uses % of parent (auto-scales with photo)
  const RoundFlag = ({ countryName, photoSize, configScreen, configElement, mirrorX = false }) => {
    const countryCode = getCountryCode(countryName);
    if (!countryCode) return null;
    
    // If wired to a screen/element, use that display_styles config; else fall back to theme.flag_size_percent.
    const flagS = (configScreen && configElement) ? ss(theme, configScreen, configElement) : null;
    const configuredPercent = flagS ? (flagS.size || 1) * 30 : (theme.flag_size_percent || 30);
    const useAbsolute = photoSize != null && !flagS;
    const flagSizeStyle = useAbsolute
      ? { width: `${photoSize * (configuredPercent / 100)}px`, height: `${photoSize * (configuredPercent / 100)}px` }
      : { width: `${configuredPercent}%`, aspectRatio: '1 / 1' };
    // Mirror offset_x for the left-side athlete so a single value in
    // Settings pushes both flags symmetrically towards / away from center.
    const ox = (flagS?.offset_x || 0) * (mirrorX ? -1 : 1);
    const oy = flagS?.offset_y || 0;
    
    return (
      <div 
        className="absolute rounded-full overflow-hidden"
        style={{
          ...flagSizeStyle,
          bottom: `${-4 + oy}px`,
          right: `${-4 + ox}px`,
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          border: '2px solid rgba(255,255,255,0.85)',
          zIndex: 10
        }}
      >
        <img
          src={`/flags/${countryCode}.svg`}
          alt={countryName}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
      </div>
    );
  };
  
  // Get display title
  const getDisplayTitle = () => {
    if (theme.display_title_auto !== false) {
      // Auto mode: use round name (or tournament name for free-form 1v1 series)
      const battle = currentBattle?.battle;
      const tournament = currentBattle?.tournament;
      if (battle?.round) {
        // 1v1 Battles series → show the category name (round="1VS1" is meaningless)
        if (tournament?.type === "1vs1_battles" && tournament?.name) {
          return tournament.name;
        }
        return getRoundDisplayName(battle.round);
      }
      return t('display.battleInProgress');
    } else {
      // Manual mode: use custom text
      return theme.display_title_text || t('display.battleInProgress');
    }
  };
  
  // Toggle fullscreen on stable container ref
  const displayRootRef = useRef(null);
  
  const toggleFullscreen = () => {
    const el = displayRootRef.current;
    if (!el) return;
    if (!getFullscreenElement()) {
      requestFullscreen(el).then((ok) => setIsFullscreen(ok || !!getFullscreenElement()));
    } else {
      exitFullscreen().then(() => setIsFullscreen(!!getFullscreenElement()));
    }
  };
  
  // Listen for fullscreen changes
  useEffect(() => onFullscreenChange(() => setIsFullscreen(!!getFullscreenElement())), []);

  // Load data with adaptive polling
  const loadData = useCallback(async () => {
    await loadCurrentBattle();
    await loadTournaments();
  }, []);

  // Initial load
  useEffect(() => {
    loadJudges(); // Load once (static data, cached)
    loadData();
  }, []);

  // Adaptive polling: FAST during battle/circle, SLOW when idle.
  // Skipped entirely while offline to keep the console clean and the layout frozen
  // on the last known good state until network is restored.
  // FAST polling triggers during ALL live timer states (pre_start, pre_countdown,
  // running, paused) plus voting/revealing, so a lost WebSocket frame is caught
  // by the next poll — no manual click on the Big Screen ever needed.
  const _battleTimerForPolling = currentBattle?.battle?.id ? battleTimers[currentBattle.battle.id] : null;
  const _circleTimerForPolling = currentCircle?.circle?.id ? circleTimers[currentCircle.circle.id] : null;
  const _liveTimerStatuses = ['pre_start', 'pre_countdown', 'running', 'paused'];
  const isBattleActive = (
    currentBattle?.battle?.status === 'voting' ||
    currentBattle?.battle?.status === 'revealing' ||
    _liveTimerStatuses.includes(_battleTimerForPolling?.status)
  );
  const isCircleActive = (
    currentCircle?.circle?.status === 'voting' ||
    currentCircle?.circle?.status === 'revealing' ||
    _liveTimerStatuses.includes(_circleTimerForPolling?.status)
  );
  const pollingDataFn = useCallback(() => {
    if (typeof navigator !== 'undefined' && !navigator.onLine) return;
    loadData();
    // Also refresh the judges list every cycle so a manager reorder
    // shows up on the Big Screen within one poll (800 ms) — no manual
    // refresh needed.
    loadJudges();
    // Belt-and-suspenders: refresh timers via REST every poll cycle too so
    // the Big Screen always converges to the server state even if some
    // WebSocket frames were dropped (browser throttling, wifi blip, ...).
    const bId = currentBattle?.battle?.id;
    const cId = currentCircle?.circle?.id;
    if (bId) refreshBattle(bId);
    if (cId) refreshCircle(cId);
  }, [loadData, currentBattle?.battle?.id, currentCircle?.circle?.id, refreshBattle, refreshCircle]);
  useAdaptivePolling(pollingDataFn, isBattleActive || isCircleActive);

  // Detect battle change and force complete refresh
  useEffect(() => {
    const currentBattleId = currentBattle?.battle?.id;
    
    if (currentBattleId && previousBattleIdRef.current && currentBattleId !== previousBattleIdRef.current) {
      loadTournaments();
      loadJudges();
      setRevealedVotes([]);
    }
    
    previousBattleIdRef.current = currentBattleId;
  }, [currentBattle?.battle?.id]);

  // When current battle/circle changes (or on first load), fetch initial timer state
  useEffect(() => {
    if (currentBattle?.battle?.id) refreshBattle(currentBattle.battle.id);
  }, [currentBattle?.battle?.id, refreshBattle]);
  useEffect(() => {
    if (currentCircle?.circle?.id) refreshCircle(currentCircle.circle.id);
  }, [currentCircle?.circle?.id, refreshCircle]);

  // Detect circle change
  useEffect(() => {
    const currentCircleId = currentCircle?.circle?.id;
    if (currentCircleId && previousCircleIdRef.current && currentCircleId !== previousCircleIdRef.current) {
      loadJudges();
    }
    previousCircleIdRef.current = currentCircleId;
  }, [currentCircle?.circle?.id]);

  const loadJudges = async () => {
    try {
      // Judges list is tiny (≤20 rows) but drives display order everywhere.
      // Do NOT cache it — a stale localStorage entry blocks live reorder on
      // the Big Screen (5-minute cache would freeze the order after every
      // reorder from the Manager).
      const res = await axios.get(`${API}/judges`);
      setJudges(res.data);
    } catch (error) {
      console.error("Error loading judges:", error);
    }
  };

  const loadTournaments = async () => {
    try {
      // Use cache for tournaments data
      const data = await cachedFetch('TOURNAMENTS', async () => {
        const res = await axios.get(`${API}/tournaments`);
        return res.data;
      });
      setTournaments(data);
    } catch (error) {
      console.error("Error loading tournaments:", error);
    }
  };

  const loadCurrentBattle = async () => {
    try {
      const res = await axios.get(`${API}/battle/current`);
      const fp = dataFingerprintRef.current;

      // Helper: is the existing displayed battle/circle's timer currently active?
      // Robustness guard: prevent flicker to "waiting" during transient API nulls when a timer is running.
      const _isActiveTimer = (kind, t) => {
        if (!t) return false;
        if (kind === 'battle') return !!t.first_athlete && ['pre_start','running','paused'].includes(t.status);
        if (kind === 'circle') return ['pre_countdown','running','paused'].includes(t.status);
        return false;
      };
      const battleTimerActive = currentBattle?.battle?.id ? _isActiveTimer('battle', battleTimersRef.current[currentBattle.battle.id]) : false;
      const circleTimerActive = currentCircle?.circle?.id ? _isActiveTimer('circle', circleTimersRef.current[currentCircle.circle.id]) : false;

      if (res.data) {
        // Cancel any pending "clear battle" grace timer — we have data.
        if (battleClearGraceRef.current) {
          clearTimeout(battleClearGraceRef.current);
          battleClearGraceRef.current = null;
        }
        const newId = res.data.battle?.id;
        const newVotes = res.data.votes || [];
        const newRevealed = newVotes.filter(v => v.revealed).length;

        if (newId !== fp.battleId || newVotes.length !== fp.battleVotes || newRevealed !== fp.battleRevealed) {
          fp.battleId = newId;
          fp.battleVotes = newVotes.length;
          fp.battleRevealed = newRevealed;
          setCurrentBattle(res.data);
          setRevealedVotes(newVotes.filter(v => v.revealed));
        }
        if (fp.circleId && !circleTimerActive) {
          // Schedule delayed clear (avoid sub-second flicker).
          if (!circleClearGraceRef.current) {
            circleClearGraceRef.current = setTimeout(() => {
              fp.circleId = null; fp.circleVotes = 0; fp.circleRevealed = 0; fp.circleStatus = null;
              setCurrentCircle(null);
              circleClearGraceRef.current = null;
            }, NULL_CLEAR_GRACE_MS);
          }
        }
      } else {
        // API says no current battle. Grace-delayed clear only if no active timer.
        if (fp.battleId && !battleTimerActive) {
          if (!battleClearGraceRef.current) {
            battleClearGraceRef.current = setTimeout(() => {
              fp.battleId = null; fp.battleVotes = 0; fp.battleRevealed = 0;
              setCurrentBattle(null);
              setRevealedVotes([]);
              battleClearGraceRef.current = null;
            }, NULL_CLEAR_GRACE_MS);
          }
        }
        try {
          const circleRes = await axios.get(`${API}/circle/current`);
          if (circleRes.data) {
            // Cancel pending circle clear — we have data.
            if (circleClearGraceRef.current) {
              clearTimeout(circleClearGraceRef.current);
              circleClearGraceRef.current = null;
            }
            const cId = circleRes.data.circle?.id;
            const cVotes = circleRes.data.votes || [];
            const cRevealed = cVotes.filter(v => v.revealed).length;
            const cStatus = circleRes.data.circle?.status;

            if (cId !== fp.circleId || cVotes.length !== fp.circleVotes || cRevealed !== fp.circleRevealed || cStatus !== fp.circleStatus) {
              fp.circleId = cId; fp.circleVotes = cVotes.length; fp.circleRevealed = cRevealed; fp.circleStatus = cStatus;
              setCurrentCircle(circleRes.data);
            }
          } else {
            // No current circle. Grace-delayed clear if no active timer.
            if (fp.circleId && !circleTimerActive) {
              if (!circleClearGraceRef.current) {
                circleClearGraceRef.current = setTimeout(() => {
                  fp.circleId = null; fp.circleVotes = 0; fp.circleRevealed = 0; fp.circleStatus = null;
                  setCurrentCircle(null);
                  circleClearGraceRef.current = null;
                }, NULL_CLEAR_GRACE_MS);
              }
            }
          }
        } catch {
          // network error: keep existing state — do not schedule any clear.
        }
      }
    } catch (error) {
      // Silent: keep last known good state, especially if a timer is active
      console.warn("Error loading current battle (transient):", error?.message || error);
    }
  };

  // Fullscreen button component
  const FullscreenBtn = () => !isFullscreen ? (
    <button
      onClick={toggleFullscreen}
      className="fixed top-4 right-4 z-50 px-3 py-1.5 text-white rounded-lg font-medium"
      style={{ fontSize: 'clamp(0.65rem, 0.85vw, 0.9rem)', opacity: 0, background: 'rgba(128,90,213,0.8)', transition: 'opacity 0.3s' }}
      onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
      onMouseLeave={(e) => e.currentTarget.style.opacity = '0'}
      data-testid="fullscreen-btn"
    >
      {t('display.fullscreen')}
    </button>
  ) : null;

  // Common background style
  const bgStyle = {
    background: theme.background_image_url
      ? `url(${getImageUrl ? getImageUrl(theme.background_image_url) : theme.background_image_url}) center/cover no-repeat fixed`
      : `linear-gradient(135deg, ${theme.gradient_start} 0%, ${theme.gradient_end} 100%)`
  };

  // ===== Determine display mode =====
  // The grace-on-null logic lives at the setCurrentBattle/setCurrentCircle
  // level (in loadCurrentBattle) — see the `battleWaitingGraceRef` guard.
  // That guarantees currentBattle/currentCircle are only nulled out after
  // >=2 s of stable emptiness, so the Big Screen never flickers to
  // "En attente de la prochaine battle..." for a sub-second transient.
  const displayMode = (!currentBattle && !currentCircle) ? 'waiting'
    : (currentCircle && !currentBattle) ? 'circle'
    : (currentBattle && (!currentBattle.athlete1 || !currentBattle.athlete2)) ? 'waiting'
    : 'battle';

  // ===== Detect if timer layout should replace the regular layout =====
  const _battleTimer = currentBattle?.battle?.id ? battleTimers[currentBattle.battle.id] : null;
  const _circleTimer = currentCircle?.circle?.id ? circleTimers[currentCircle.circle.id] : null;
  // The 3-2-1 pre-countdown must ALWAYS take over the display, regardless of
  // the `countdown_mode` setting — otherwise events without segment countdown
  // enabled would show nothing on the Big Screen when the manager starts.
  // IMPORTANT: `pre_start` (manager clicked Start but hasn't picked who goes
  // first yet) is NOT included here — we want the two athlete cards to still
  // show during that phase.
  const _preCountdownActive = (
    _battleTimer?.status === 'pre_countdown' ||
    _circleTimer?.status === 'pre_countdown'
  );
  const timerLayoutActive = _preCountdownActive || (countdownMode && (
    (currentBattle && isTimerLayoutActive("battle", _battleTimer)) ||
    (currentCircle && isTimerLayoutActive("circle", _circleTimer))
  ));

  // ===== Pre-compute circle data (needed even if not in circle mode) =====
  let circleData = null;
  if (displayMode === 'circle') {
    const circle = currentCircle.circle;
    const allAthletes = currentCircle.athletes;
    const forfeited = circle.forfeited_ids || [];
    const votes = currentCircle.votes || [];
    const revealedCircleVotes = votes.filter(v => v.revealed);
    const tournamentInfo = currentCircle.tournament;
    const judgesCount = tournamentInfo?.judges_count || 5;
    const assignedJudgeNumbers = tournamentInfo?.assigned_judge_numbers || [];
    const _rawEffective = assignedJudgeNumbers.length > 0 ? assignedJudgeNumbers : Array.from({length: judgesCount}, (_, i) => i + 1);
    const effectiveJudgeNumbers = sortJudgeNumbers(_rawEffective, judges);
    const athleteColor = theme.display_score_color || '#a78bfa';
    // Dedicated circle colors with fallback to display_score_color for backward compat
    const circlePositionLabelColor = theme.circle_position_label_color || athleteColor;
    const circleScoreColor = theme.circle_score_color || athleteColor;
    const totals = {};
    allAthletes.forEach(a => { if (a) totals[a.id] = 0; });
    revealedCircleVotes.forEach(v => { Object.entries(v.scores || {}).forEach(([aid, score]) => { if (totals[aid] !== undefined) totals[aid] += score; }); });
    forfeited.forEach(fid => { totals[fid] = 0; });
    const circleWinner = circle.winner_id;
    const activeAthletes = allAthletes.filter(a => a && !forfeited.includes(a.id));
    const numCols = allAthletes.filter(a => a).length;
    const allVotesRevealed = votes.length > 0 && votes.every(v => v.revealed);
    const rankings = {};
    let calculatedWinner = null;
    if (allVotesRevealed) {
      const sorted = [...activeAthletes].sort((a, b) => { const diff = (totals[b.id] || 0) - (totals[a.id] || 0); if (diff !== 0) return diff; return (a.world_ranking || 9999) - (b.world_ranking || 9999); });
      sorted.forEach((a, i) => { rankings[a.id] = i + 1; });
      forfeited.forEach(fid => { rankings[fid] = sorted.length + 1; });
      if (sorted.length > 0) calculatedWinner = sorted[0].id;
    }
    const effectiveWinner = calculatedWinner || circleWinner;
    const rankLabels = [t('display.rank1'), t('display.rank2'), t('display.rank3'), t('display.rank4')];
    const judgeGapMultiplier = (theme.display_judge_gap || 100) / 100;
    const circleTitleSize = theme.circle_title_size || 0.7;
    const judgeNameSize = theme.display_judge_name_size || 1;
    const anyVoteRevealed = votes.some(v => v.revealed);
    circleData = { circle, allAthletes, forfeited, votes, revealedCircleVotes, effectiveJudgeNumbers, athleteColor, circlePositionLabelColor, circleScoreColor, totals, activeAthletes, numCols, allVotesRevealed, rankings, effectiveWinner, rankLabels, circleTitleSize, judgeNameSize, anyVoteRevealed };
  }

  // ===== Pre-compute battle data =====
  let battleData = null;
  if (displayMode === 'battle') {
    const athlete1Total = revealedVotes.reduce((sum, v) => sum + v.athlete1_score, 0);
    const athlete2Total = revealedVotes.reduce((sum, v) => sum + v.athlete2_score, 0);
    const battleTournament = tournaments.find(t => t.id === currentBattle.battle?.tournament_id);
    // Prefer the judges array actually returned by /api/battle/current — it's
    // the authoritative list for this specific battle. This is what makes 1v1
    // battles honour the tournament's judges_count exactly like brackets do:
    // if the tournament only assigned 3 judges, the payload contains 3 judge
    // entries and we render 3 slots. Fallback to tournament.judges_count for
    // legacy responses that did not yet include the judges array.
    const battleJudges = Array.isArray(currentBattle.judges) ? currentBattle.judges : null;
    const tournamentJudgesCount = battleTournament?.judges_count || 5;
    const _rawAssigned = battleJudges
      ? battleJudges.map(j => j.judge_number)
      : ((battleTournament?.assigned_judge_numbers?.length > 0)
          ? battleTournament.assigned_judge_numbers
          : Array.from({length: tournamentJudgesCount}, (_, i) => i + 1));
    const assignedJudges = sortJudgeNumbers(_rawAssigned, judges);
    const expectedJudgesCount = assignedJudges.length;
    const allRevealed = revealedVotes.length === expectedJudgesCount && revealedVotes.every(v => v.revealed);
    let winner = null, winnerType = null;
    if (allRevealed) {
      if (athlete1Total > athlete2Total) { winner = 'athlete1'; winnerType = 'score'; }
      else if (athlete2Total > athlete1Total) { winner = 'athlete2'; winnerType = 'score'; }
      else { const a1w = revealedVotes.filter(v => v.athlete1_score > v.athlete2_score).length; const a2w = revealedVotes.filter(v => v.athlete2_score > v.athlete1_score).length; if (a1w > a2w) { winner = 'athlete1'; winnerType = 'judge_count'; } else if (a2w > a1w) { winner = 'athlete2'; winnerType = 'judge_count'; } }
    }
    battleData = { athlete1Total, athlete2Total, battleTournament, assignedJudges, expectedJudgesCount, allRevealed, winner, winnerType };
  }

  // ===== SINGLE RETURN - The ref container NEVER unmounts =====
  return (
    <div ref={displayRootRef} className="h-screen w-screen overflow-hidden relative" style={{...bgStyle, textShadow: '0 2px 4px rgba(0,0,0,0.5), 0 4px 12px rgba(0,0,0,0.65), 0 0 32px rgba(0,0,0,0.35)'}} data-testid="display-root">
      <div
        data-testid="display-canvas"
        style={{
          position: "absolute",
          width: `${designWidth}px`,
          height: `${designHeight}px`,
          left: `${displayFit.offsetX}px`,
          top: `${displayFit.offsetY}px`,
          transform: `scale(${displayFit.scale})`,
          transformOrigin: "top left",
        }}
      >
      <style>{`
        @keyframes pulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.85; transform: scale(1.03); } }
        ${theme.background_image_url ? '.glass { background: rgba(0, 0, 0, 0.7) !important; backdrop-filter: blur(10px); }' : ''}
        body, html { overflow: hidden !important; width: 100vw; height: 100vh; }
        /* Inside the design canvas, treat vw/vh-like units as ratios of the
           design size, so the layout fits the fixed canvas dimensions. */
        /* heightFactor: when the canvas is shorter than 16:9, shrink height-
           dependent dimensions (photo, fonts, paddings) proportionally so the
           full circle reveal panel still fits without clipping (e.g. 1792x768
           is only ~76% of a true 16:9 height of 1008, so heightFactor=0.76). */
        ${(() => {
          return `
        [data-testid="display-canvas"] .display-container {
          padding: ${designHeight * 0.01}px ${designWidth * 0.01}px;
          height: ${designHeight}px;
          width: ${designWidth}px;
          display: flex; flex-direction: column; justify-content: center;
          max-width: ${designWidth}px;
          overflow: hidden;
        }
        [data-testid="display-canvas"] .display-title { margin-bottom: ${designHeight * 0.005}px; }
        [data-testid="display-canvas"] .athlete-card { padding: ${designHeight * 0.015}px ${designWidth * 0.01}px; }
        [data-testid="display-canvas"] .circle-athlete-card { padding: ${designHeight * 0.012 * heightFactor}px ${designWidth * 0.008}px; }
        [data-testid="display-canvas"] .athlete-photo { width: ${Math.min(designWidth * 0.08, 160) * heightFactor}px; height: ${Math.min(designWidth * 0.08, 160) * heightFactor}px; }
        [data-testid="display-canvas"] .athlete-name { font-size: ${Math.min(designWidth * 0.02, 40) * heightFactor}px; font-family: 'Montserrat', sans-serif; }
        [data-testid="display-canvas"] .athlete-country { font-size: ${Math.min(designWidth * 0.015, 24) * heightFactor}px; font-family: 'Montserrat', sans-serif; }
        [data-testid="display-canvas"] .athlete-ranking { font-size: ${Math.min(designWidth * 0.0175, 32) * heightFactor}px; font-family: 'Montserrat', sans-serif; }
        [data-testid="display-canvas"] .score-large { font-size: ${Math.min(designWidth * 0.06, 128) * heightFactor}px; font-family: 'Montserrat', sans-serif; line-height: 1; }
        [data-testid="display-canvas"] .score-small { font-size: ${Math.min(designWidth * 0.04, 96) * heightFactor}px; font-family: 'Montserrat', sans-serif; line-height: 1; }
        [data-testid="display-canvas"] .vs-text { font-size: ${Math.min(designWidth * 0.05, 96)}px; font-family: 'Bebas Neue', sans-serif; }
        [data-testid="display-canvas"] .judge-card { padding: ${designHeight * 0.01}px ${designWidth * 0.005}px; min-height: ${Math.min(designHeight * 0.15, 180)}px; }
        [data-testid="display-canvas"] .judge-icon { width: ${Math.min(designWidth * 0.03, 56) * heightFactor}px; height: ${Math.min(designWidth * 0.03, 56) * heightFactor}px; }
        [data-testid="display-canvas"] .judge-name { font-size: ${Math.min(designWidth * 0.0125, 24) * heightFactor}px; font-family: 'Montserrat', sans-serif; }
        [data-testid="display-canvas"] .judge-score { font-size: ${Math.min(designWidth * 0.02, 48) * heightFactor}px; font-family: 'Montserrat', sans-serif; }
        [data-testid="display-canvas"] .judge-status { font-size: ${Math.min(designWidth * 0.01, 16) * heightFactor}px; }
        /* Compact mode active when heightFactor < 0.9: collapse marginBottom and minHeight to recover space */
        ${heightFactor < 0.9 ? `
        [data-testid="display-canvas"] .circle-athlete-card > * { margin-bottom: ${2 * heightFactor}px !important; }
        [data-testid="display-canvas"] .judge-card { min-height: ${Math.min(designHeight * 0.12, 140)}px; }
        ` : ''}
          `;
        })()}
      `}</style>

      <FullscreenBtn />

      {simulationMode && (
        <div
          className="absolute top-0 left-0 right-0 z-50 text-center py-2 font-bold text-white tracking-widest text-lg uppercase"
          style={{
            background: "linear-gradient(90deg, #f97316, #ea580c, #f97316)",
            boxShadow: "0 2px 12px rgba(249,115,22,0.6)",
            animation: "pulse 2s ease-in-out infinite",
            letterSpacing: "0.3em",
          }}
          data-testid="display-simulation-banner"
        >
          {t("dryRun.bannerText")}
        </div>
      )}

      {/* Timer active layout (replaces the regular battle/circle render when timer is engaged) */}
      {(() => {
        if (!countdownMode) return null;
        const battleTimer = currentBattle?.battle?.id ? battleTimers[currentBattle.battle.id] : null;
        const circleTimer = currentCircle?.circle?.id ? circleTimers[currentCircle.circle.id] : null;
        if (currentBattle && isTimerLayoutActive("battle", battleTimer)) {
          return (
            <TimerActiveDisplay
              kind="battle"
              timer={battleTimer}
              athlete1={currentBattle.athlete1}
              athlete2={currentBattle.athlete2}
              theme={theme}
              soundEnabled={countdownSoundEnabled}
            />
          );
        }
        if (currentCircle && isTimerLayoutActive("circle", circleTimer)) {
          return (
            <TimerActiveDisplay
              kind="circle"
              timer={circleTimer}
              athletes={currentCircle.athletes || []}
              currentCircle={currentCircle}
              positionOrder={countdownSettings.circle_position_order}
              theme={theme}
              soundEnabled={countdownSoundEnabled}
            />
          );
        }
        return null;
      })()}

      {/* ===== WAITING STATE ===== */}
      {displayMode === 'waiting' && !timerLayoutActive && (
        <div className="h-full w-full flex items-center justify-center p-6">
          <Card className="p-12 glass text-center" data-testid="no-battle-display" style={{ background: theme.background_image_url ? 'rgba(0, 0, 0, 0.7)' : undefined }}>
            <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
              <span style={{ color: theme.battle_voting_system_color || '#ffffff' }}>{t('display.waitingTitle')}</span>
            </h1>
            <p className="text-2xl text-gray-400">{t('display.waitingSubtitle')}</p>
          </Card>
        </div>
      )}

      {/* ===== CIRCLE DISPLAY ===== */}
      {displayMode === 'circle' && circleData && !timerLayoutActive && (() => {
        const { circle, allAthletes, forfeited, votes, revealedCircleVotes, effectiveJudgeNumbers, athleteColor, circlePositionLabelColor, circleScoreColor, totals, activeAthletes, numCols, allVotesRevealed, rankings, effectiveWinner, rankLabels, circleTitleSize, judgeNameSize, anyVoteRevealed } = circleData;
        // Tighten title and vertical gaps on shorter displays (heightFactor < 1)
        const titleSize = `clamp(${1.5 * circleTitleSize * heightFactor}rem, ${3 * circleTitleSize * heightFactor}vw, ${4 * circleTitleSize * heightFactor}rem)`;
        const gridMarginBottom = `${heightFactor * 1}vh`;
        const titleMarginBottom = `${heightFactor * 0.5}vh`;
        const judgePanelPadding = `${heightFactor * 1}vh ${heightFactor * 1.5}vw`;
        const judgeRowPadding = `${heightFactor * 0.3}vh ${heightFactor * 1.2}vw`;
        const judgeColMinWidth = `clamp(${60 * heightFactor}px, ${10 * heightFactor}vw, ${160 * heightFactor}px)`;
        const judgeNameFontSize = `clamp(${0.75 * judgeNameSize * heightFactor}rem, ${1.25 * judgeNameSize * heightFactor}vw, ${1.5 * judgeNameSize * heightFactor}rem)`;
        const judgeStatusFontSize = `clamp(${0.55 * heightFactor}rem, ${0.9 * heightFactor}vw, ${0.9 * heightFactor}rem)`;
        return (        <div className="display-container">
          {/* Title */}
          {theme.display_title_show !== false && (() => {
            const titleS = ss(theme, 'circle_vote', 'circle_title', { color: theme.display_title_color });
            return (
              <h1
                className="font-bold text-center uppercase"
                style={{
                  fontFamily: 'Bebas Neue, sans-serif',
                  fontSize: `calc(${titleSize} * ${titleS.size})`,
                  transition: 'font-size 0.3s ease',
                  color: titleS.color || '#ffffff',
                  marginBottom: titleMarginBottom,
                  transform: offsetTransform(titleS),
                  textShadow: titleS.textShadow,
                }}
              >
                {t('circle.title')} {circle.circle_number}
              </h1>
            );
          })()}

          {/* Athletes Grid - same card style as battles */}
          <div className="relative flex-shrink-0" style={{ marginBottom: gridMarginBottom }}>
            <div 
              className={`grid grid-cols-${numCols} relative mx-auto`}
              style={{ 
                gap: '3px',
                maxWidth: `${(theme.display_athlete_card_width || 100)}%`,
                gridTemplateColumns: `repeat(${numCols}, 1fr)`
              }}
            >
              {allAthletes.map((athlete, idx) => {
                if (!athlete) return null;
                const isWinner = effectiveWinner === athlete.id;
                const isForfeited = forfeited.includes(athlete.id);
                const score = totals[athlete.id] || 0;
                const cardBgS = ss(theme, 'circle_vote', 'card_background');
                const photoS = ss(theme, 'circle_vote', 'athlete_photo');
                const scoreS = ss(theme, 'circle_vote', 'athlete_score', { color: circleScoreColor });
                const cardW = cardBgS.width ?? 1;
                const cardH = cardBgS.height ?? 1;

                return (
                  <Card
                    key={athlete.id}
                    className={`circle-athlete-card glass text-center transition-all duration-1000 ${
                      effectiveWinner && !isWinner && !isForfeited ? 'opacity-60 scale-95' : ''
                    }`}
                    data-testid={`circle-display-athlete-${athlete.id}`}
                    style={{
                      border: isWinner ? `clamp(3px, 0.5vw, 6px) solid ${winnerHighlightColor}` : (cardBgS.border_color ? `1px solid ${cardBgS.border_color}` : 'none'),
                      boxShadow: isWinner
                        ? `0 0 50px ${winnerHighlightColor}e6, inset 0 0 30px ${winnerHighlightColor}33, 0 0 100px ${winnerHighlightColor}40`
                        : 'none',
                      background: isWinner
                        ? `linear-gradient(180deg, ${winnerHighlightColor}15 0%, transparent 50%)`
                        : (cardBgS.bg_color || undefined),
                      transform: `translate(${cardBgS.offset_x || 0}px, ${cardBgS.offset_y || 0}px) ${isWinner ? 'scale(1.03)' : (effectiveWinner && !isForfeited ? 'scale(0.95)' : '')}`.trim(),
                      width: cardW !== 1 ? `${cardW * 100}%` : undefined,
                      minHeight: cardH !== 1 ? `${cardH * 100}%` : undefined,
                      opacity: isForfeited ? 0.5 : undefined,
                      filter: isForfeited ? 'grayscale(0.6)' : 'none'
                    }}
                  >
                    {/* Position label */}
                    <div 
                      className="font-bold uppercase tracking-wider"
                      style={{ 
                        fontFamily: 'Montserrat, sans-serif',
                        fontSize: `clamp(${0.6 * heightFactor * ss(theme,'circle_vote','position_label').size}rem, ${0.9 * heightFactor * ss(theme,'circle_vote','position_label').size}vw, ${0.95 * heightFactor * ss(theme,'circle_vote','position_label').size}rem)`,
                        color: ss(theme,'circle_vote','position_label',{color: circlePositionLabelColor}).color,
                        letterSpacing: '0.15em',
                        marginBottom: `${heightFactor * 0.4}vh`,
                        transform: offsetTransform(ss(theme,'circle_vote','position_label')),
                        textShadow: ss(theme,'circle_vote','position_label').textShadow,
                      }}
                    >
                      {t('circle.position').replace('{num}', idx + 1)}
                    </div>

                    {/* Photo - always show (real photo or initial fallback) */}
                    <div className="relative inline-block" style={{ marginBottom: `${heightFactor * 0.4}vh`, transform: `scale(${photoS.size}) ${offsetTransform(photoS)}`, transformOrigin: 'center' }}>
                      {athlete.photo_url ? (
                        <img
                          src={getImageUrl(athlete.photo_url)}
                          alt={athlete.name}
                          className="athlete-photo rounded-full mx-auto object-cover"
                          style={{
                            border: theme.photo_border_show !== false
                              ? `3px solid ${isForfeited ? '#555' : (isWinner ? winnerHighlightColor : (photoS.border_color || theme.athlete_photo_border_color || '#ffffff'))}`
                              : 'none'
                          }}
                        />
                      ) : (
                        <div
                          className="athlete-photo rounded-full mx-auto flex items-center justify-center font-bold"
                          style={{
                            background: isForfeited ? '#1a1a2e' : (photoS.bg_color || `${circleScoreColor}20`),
                            border: `3px solid ${isForfeited ? '#555' : (isWinner ? winnerHighlightColor : (photoS.border_color || theme.athlete_photo_border_color || '#ffffff'))}`,
                            color: isForfeited ? '#555' : (photoS.color || circleScoreColor),
                            fontSize: 'clamp(1.5rem, 3vw, 3rem)'
                          }}
                        >
                          {athlete.name?.charAt(0)}
                        </div>
                      )}
                      {!isForfeited && <RoundFlag countryName={athlete.country} configScreen="circle_vote" configElement="athlete_flag" />}
                    </div>

                    {/* Name + WR (same pattern as 1v1 battles) */}
                    <div className="flex items-center justify-center gap-2" style={{ marginBottom: `${heightFactor * 0.2}vh` }}>
                      <h2 
                        className={`athlete-name font-bold uppercase leading-tight ${isForfeited ? 'line-through' : ''}`} 
                        style={{
                          fontFamily: 'Montserrat, sans-serif',
                          color: isForfeited ? '#666' : ss(theme,'circle_vote','athlete_name',{color:'white'}).color,
                          transform: offsetTransform(ss(theme,'circle_vote','athlete_name')),
                          fontSize: `calc(1em * ${ss(theme,'circle_vote','athlete_name').size})`,
                          textShadow: ss(theme,'circle_vote','athlete_name').textShadow,
                        }}
                      >
                        {athlete.name}
                      </h2>
                      {!isForfeited && (
                        <span
                          className="athlete-ranking font-bold"
                          style={{
                            color: ss(theme,'circle_vote','athlete_wr',{color: theme.display_wr_color || '#fbbf24'}).color,
                            transform: offsetTransform(ss(theme,'circle_vote','athlete_wr')),
                            fontSize: `calc(1em * ${ss(theme,'circle_vote','athlete_wr').size})`,
                            textShadow: ss(theme,'circle_vote','athlete_wr').textShadow,
                          }}
                        >
                          {athlete.world_ranking ? `#${athlete.world_ranking}` : 'N/A'}
                        </span>
                      )}
                    </div>

                    {/* Score or Forfait */}
                    {isForfeited ? (
                      <div style={{ fontFamily: 'Montserrat, sans-serif', marginTop: `${heightFactor * 0.4}vh` }}>
                        <div className="font-bold text-red-500/70 uppercase tracking-wider" style={{ fontSize: `clamp(${0.9 * heightFactor}rem, ${1.8 * heightFactor}vw, ${1.8 * heightFactor}rem)` }}>{t('display.forfait')}</div>
                      </div>
                    ) : (
                      <div style={{ marginTop: `${heightFactor * 0.3}vh`, transform: offsetTransform(scoreS) }}>
                        <div
                          className={isWinner ? 'score-large' : 'score-small'}
                          style={{
                            color: isWinner ? winnerHighlightColor : (scoreS.color || circleScoreColor),
                            fontWeight: 'bold',
                            fontSize: `calc(1em * ${scoreS.size})`,
                            textShadow: isWinner
                              ? `0 0 20px ${winnerHighlightColor}cc, 0 0 40px ${winnerHighlightColor}80`
                              : scoreS.textShadow,
                          }}
                        >
                          {score}
                        </div>
                        {allVotesRevealed && rankings[athlete.id] && (
                          <div 
                            className="font-bold uppercase tracking-wider" 
                            style={{ 
                              color: isWinner ? winnerHighlightColor : circleScoreColor,
                              fontSize: `clamp(${0.7 * heightFactor}rem, ${1.3 * heightFactor}vw, ${1.4 * heightFactor}rem)`,
                              fontFamily: 'Montserrat, sans-serif'
                            }}
                          >
                            {rankLabels[rankings[athlete.id] - 1] || `${rankings[athlete.id]}th`}
                          </div>
                        )}
                        {isWinner && (
                          <div className="font-bold" style={{ color: winnerHighlightColor, fontSize: `clamp(${0.55 * heightFactor}rem, ${0.9 * heightFactor}vw, ${0.9 * heightFactor}rem)` }}>
                            {t('display.winner')}
                          </div>
                        )}
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Judges Section - Unified Grid */}
          <div className="flex justify-center flex-shrink-0">
            {(() => {
              const panelS = ss(theme, 'circle_vote', 'judges_panel_bg');
              const pW = panelS.width ?? 1;
              const pH = panelS.height ?? 1;
              return (
                <div
                  className="rounded-lg"
                  style={{
                    padding: judgePanelPadding,
                    background: panelS.bg_color || 'rgba(0,0,0,0.45)',
                    border: `1px solid ${panelS.border_color || 'rgba(255,255,255,0.1)'}`,
                    backdropFilter: 'blur(10px)',
                    transform: offsetTransform(panelS),
                    width: pW !== 1 ? `${pW * 100}%` : undefined,
                    minHeight: pH !== 1 ? `${pH * 100}%` : undefined,
                  }}
                >
                  {(() => {
                // Pre-compute best & 2nd best score per judge for highlighting
                const judgeBestScores = {};
                effectiveJudgeNumbers.forEach(jn => {
                  const vote = votes.find(v => v.judge_number === jn);
                  if (!vote?.revealed) return;
                  const scores = allAthletes
                    .filter(a => a && !forfeited.includes(a.id))
                    .map(a => vote.scores?.[a.id] || 0)
                    .sort((x, y) => y - x);
                  judgeBestScores[jn] = { best: scores[0] ?? -1, second: scores[1] ?? -1 };
                });

                return (
                  <div 
                    style={{
                      display: 'grid',
                      gridTemplateColumns: `auto repeat(${effectiveJudgeNumbers.length}, 1fr)`,
                      alignItems: 'center',
                      gap: '0'
                    }}
                  >
                    {/* Header row: empty cell + judge columns */}
                    <div></div>
                    {effectiveJudgeNumbers.map(jn => {
                      const vote = votes.find(v => v.judge_number === jn);
                      const revealed = vote?.revealed;
                      const hasVoted = !!vote;
                      const judge = judges.find(j => j.judge_number === jn);

                      return (
                        <div 
                          key={jn} 
                          className="text-center"
                          data-testid={`circle-display-judge-${jn}`}
                          style={{ padding: judgeRowPadding, minWidth: judgeColMinWidth }}
                        >
                          <div style={{ marginBottom: `${heightFactor * 0.3}vh` }}>
                            {(() => {
                              const dotS = ss(theme, 'circle_vote', 'judge_dot');
                              const dotStyle = { transform: `scale(${dotS.size}) ${offsetTransform(dotS)}`, transformOrigin: 'center' };
                              if (revealed) return <CheckCircle className="judge-icon mx-auto" style={{ color: dotS.color || theme.judge_revealed_color || '#22c55e', ...dotStyle }} />;
                              if (hasVoted) return <CheckCircle className="judge-icon mx-auto" style={{ color: dotS.color || theme.judge_voted_color || '#eab308', ...dotStyle }} />;
                              return <Circle className="judge-icon mx-auto" style={{ color: dotS.color || theme.judge_waiting_color || '#6b7280', ...dotStyle }} />;
                            })()}
                          </div>
                          {(() => {
                            const nameS = ss(theme, 'circle_vote', 'judge_name');
                            return (
                              <h4
                                className="font-bold uppercase"
                                style={{
                                  color: nameS.color || 'white',
                                  fontFamily: 'Montserrat, sans-serif',
                                  fontSize: `calc(${judgeNameFontSize} * ${nameS.size})`,
                                  transition: 'font-size 0.3s ease',
                                  transform: offsetTransform(nameS),
                                  textShadow: nameS.textShadow,
                                }}
                              >
                                {judge?.first_name || `Judge ${jn}`}
                              </h4>
                            );
                          })()}
                          {!anyVoteRevealed && (() => {
                            const stS = ss(theme, 'circle_vote', 'judge_status');
                            return (
                              <p
                                className="judge-status"
                                style={{
                                  color: stS.color || theme.judge_status_text || '#9ca3af',
                                  fontSize: `calc(${judgeStatusFontSize} * ${stS.size})`,
                                  transform: offsetTransform(stS),
                                  textShadow: stS.textShadow,
                                }}
                              >
                                {hasVoted ? t('display.voted') : t('display.waiting')}
                              </p>
                            );
                          })()}
                        </div>
                      );
                    })}

                    {/* Score rows: POS label + scores per judge */}
                    {anyVoteRevealed && allAthletes.map((athlete, posIdx) => {
                      if (!athlete) return null;
                      const isAthleteForfeited = forfeited.includes(athlete.id);
                      const rank = rankings[athlete.id];
                      const isRowWinner = rank === 1 && allVotesRevealed;

                      return (
                        <React.Fragment key={athlete.id}>
                          <div 
                            style={{ 
                              padding: `${heightFactor * 0.4}vh ${heightFactor * 1.2}vw ${heightFactor * 0.4}vh ${heightFactor * 0.5}vw`,
                              fontFamily: 'Montserrat, sans-serif',
                              fontSize: `clamp(${0.6 * heightFactor}rem, ${1 * heightFactor}vw, ${1.1 * heightFactor}rem)`,
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              color: isAthleteForfeited ? '#555' : (isRowWinner ? winnerHighlightColor : 'rgba(255,255,255,0.6)'),
                              whiteSpace: 'nowrap'
                            }}
                          >
                            POS {posIdx + 1}
                          </div>
                          {effectiveJudgeNumbers.map(jn => {
                            const vote = votes.find(v => v.judge_number === jn);
                            const revealed = vote?.revealed;
                            const score = revealed ? (isAthleteForfeited ? 0 : (vote?.scores?.[athlete.id] || 0)) : null;
                            const best = judgeBestScores[jn];
                            
                            let scoreColor = athleteColor;
                            if (revealed && !isAthleteForfeited && best) {
                              if (score === best.best) scoreColor = winnerHighlightColor;
                              else if (score === best.second && best.second !== best.best) scoreColor = '#f59e0b';
                            }
                            if (isAthleteForfeited) scoreColor = '#555';

                            return (
                              <div 
                                key={jn}
                                style={{
                                  textAlign: 'center',
                                  padding: `${heightFactor * 0.4}vh ${heightFactor * 0.5}vw`,
                                  fontFamily: 'Montserrat, sans-serif',
                                  fontSize: `clamp(${0.9 * heightFactor}rem, ${1.6 * heightFactor}vw, ${2 * heightFactor}rem)`,
                                  fontWeight: 700,
                                  color: !revealed ? 'transparent' : scoreColor,
                                  opacity: isAthleteForfeited ? 0.4 : 1,
                                  transition: 'color 0.5s ease'
                                }}
                              >
                                {revealed ? score : '-'}
                              </div>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </div>
                );
              })()}
                </div>
              );
            })()}
          </div>
        </div>
        );
      })()}

      {/* ===== BATTLE DISPLAY ===== */}
      {displayMode === 'battle' && battleData && !timerLayoutActive && (() => {
        const { athlete1Total, athlete2Total, assignedJudges, expectedJudgesCount, allRevealed, winner, winnerType } = battleData;
        return (
      <div className="display-container">
        {theme.display_title_show !== false && (() => {
          const titleS = ss(theme, 'battle_vote', 'battle_title', { color: theme.display_title_color });
          return (
            <h1
              className="display-title font-bold text-center uppercase"
              style={{
                fontFamily: 'Bebas Neue, sans-serif',
                fontSize: `clamp(${1.5 * (theme.display_title_size || 1) * titleS.size}rem, ${3 * (theme.display_title_size || 1) * titleS.size}vw, ${4 * (theme.display_title_size || 1) * titleS.size}rem)`,
                transition: 'font-size 0.3s ease',
                color: titleS.color || '#ffffff',
                transform: offsetTransform(titleS),
                textShadow: titleS.textShadow,
              }}
            >
              {getDisplayTitle()}
            </h1>
          );
        })()}

        {/* Athletes with scores - Grid layout with adjustable width */}
        <div className="relative flex-shrink-0" style={{ marginBottom: '1vh' }}>
          <div 
            className="grid grid-cols-2 relative mx-auto items-start" 
            style={{ 
              gap: '0px',
              width: '100%',
              maxWidth: `${(theme.display_athlete_card_width || 100)}%`
            }}
          >
            {/* Athlete 1 Card */}
            {(() => {
              const cardBgS = ss(theme, 'battle_vote', 'card_background');
              const photoS = ss(theme, 'battle_vote', 'athlete_photo');
              const cardW = cardBgS.width ?? 1;
              const cardH = cardBgS.height ?? 1;
              return (
            <Card
              className={`athlete-card glass text-center transition-all duration-1000 ${
                winner === 'athlete2' ? 'opacity-70 scale-95' : ''
              }`}
              data-testid="athlete1-display"
              style={{
                border: winner === 'athlete1'
                  ? `clamp(3px, 0.5vw, 6px) solid ${winnerHighlightColor}`
                  : (cardBgS.border_color ? `1px solid ${cardBgS.border_color}` : 'none'),
                background: winner === 'athlete1' ? undefined : (cardBgS.bg_color || undefined),
                boxShadow: winner === 'athlete1' ? `0 0 50px ${winnerHighlightColor}e6, inset 0 0 30px ${winnerHighlightColor}33` : 'none',
                width: cardW !== 1 ? `${cardW * 100}%` : '100%',
                minHeight: cardH !== 1 ? `${cardH * 100}%` : undefined,
                // The card hugs the CENTER edge of its grid cell (right edge of the LEFT cell),
                // so reducing its width no longer creates dead space between the two cards.
                // The symmetric offset_x then pushes it AWAY from the center by N pixels.
                justifySelf: 'end',
                marginRight: 0,
                transform: `translate(${-(cardBgS.offset_x || 0)}px, ${cardBgS.offset_y || 0}px)`,
              }}
            >
              <div className="relative inline-block" style={{ marginBottom: '0.5vh', transform: `scale(${photoS.size}) ${offsetTransformMirrored(photoS, true)}`, transformOrigin: 'center' }}>
                {currentBattle.athlete1.photo_url ? (
                  <img
                    src={getImageUrl(currentBattle.athlete1.photo_url)}
                    alt={currentBattle.athlete1.name}
                    className="athlete-photo rounded-full mx-auto object-cover"
                    style={{
                      border: theme.photo_border_show !== false
                        ? `3px solid ${winner === 'athlete1' ? winnerHighlightColor : (photoS.border_color || theme.athlete_photo_border_color || '#ffffff')}`
                        : 'none'
                    }}
                  />
                ) : (
                  <div
                    className="athlete-photo rounded-full mx-auto flex items-center justify-center font-bold"
                    style={{
                      background: photoS.bg_color || `${athlete1Color}20`,
                      border: `3px solid ${winner === 'athlete1' ? winnerHighlightColor : (photoS.border_color || theme.athlete_photo_border_color || '#ffffff')}`,
                      color: photoS.color || athlete1Color,
                      fontSize: 'clamp(1.5rem, 3vw, 3rem)'
                    }}
                  >
                    {currentBattle.athlete1.name?.charAt(0)}
                  </div>
                )}
                <RoundFlag
                  countryName={currentBattle.athlete1.country}
                  configScreen="battle_vote"
                  configElement="athlete_flag"
                  mirrorX={true}
                />
              </div>
              <div className="flex items-center justify-center gap-2" style={{ marginBottom: '0.25vh' }}>
                <h2
                  className="athlete-name font-bold uppercase leading-tight"
                  style={{
                    fontFamily: 'Montserrat, sans-serif',
                    color: ss(theme,'battle_vote','athlete_name',{color:'white'}).color,
                    transform: offsetTransformMirrored(ss(theme,'battle_vote','athlete_name'), true),
                    fontSize: `calc(1em * ${ss(theme,'battle_vote','athlete_name').size})`,
                    textShadow: ss(theme,'battle_vote','athlete_name').textShadow,
                  }}
                >
                  {currentBattle.athlete1.name}
                </h2>
                <span
                  className="athlete-ranking font-bold"
                  style={{
                    color: ss(theme,'battle_vote','athlete_wr',{color: theme.display_wr_color || '#fbbf24'}).color,
                    transform: offsetTransformMirrored(ss(theme,'battle_vote','athlete_wr'), true),
                    fontSize: `calc(1em * ${ss(theme,'battle_vote','athlete_wr').size})`,
                    textShadow: ss(theme,'battle_vote','athlete_wr').textShadow,
                  }}
                >
                  {currentBattle.athlete1.world_ranking ? `#${currentBattle.athlete1.world_ranking}` : 'N/A'}
                </span>
              </div>
              {winner === 'athlete1' && winnerType === 'judge_count' && null /* moved out of the card so its position is not clipped — see the absolute overlay below */}
            </Card>
              );
            })()}

            {/* VS and Scores in Center — anchored to the OUTER relative container, so their position never depends on the athlete cards' width. */}
            <div 
              className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 items-center justify-center z-10 pointer-events-none"
              style={{ gap: 'clamp(1rem, 2vw, 3rem)' }}
            >
              {/* Score Athlete 1 */}
              {revealedVotes.length > 0 && (
                <div 
                  className={winner === 'athlete1' ? 'score-large' : 'score-small'}
                  style={{
                    color: winner === 'athlete1' ? winnerHighlightColor : athlete1Color,
                    minWidth: 'clamp(3rem, 6vw, 6rem)',
                    textAlign: 'right',
                    fontWeight: 'bold',
                    textShadow: winner === 'athlete1' ? `0 0 20px ${winnerHighlightColor}cc, 0 0 40px ${winnerHighlightColor}80` : undefined
                  }}
                >
                  {athlete1Total}
                </div>
              )}
              
              {/* VS */}
              {(() => {
                const vsS = ss(theme, 'battle_vote', 'vs_label', { color: theme.display_vs_color });
                const legacyPct = (() => { const v = parseInt(theme.display_vs_size); return (v >= 50 && v <= 200) ? v : 100; })();
                return (
                  <span
                    className="vs-text font-black uppercase"
                    style={{
                      color: vsS.color || '#FFFFFF',
                      textShadow: vsS.textShadow,
                      letterSpacing: '0.1em',
                      transform: `scale(${(legacyPct / 100) * (vsS.size || 1)}) ${offsetTransform(vsS)}`.trim(),
                      transformOrigin: 'center',
                      display: 'inline-block'
                    }}
                  >
                    VS
                  </span>
                );
              })()}
              
              {/* Score Athlete 2 */}
              {revealedVotes.length > 0 && (
                <div 
                  className={winner === 'athlete2' ? 'score-large' : 'score-small'}
                  style={{
                    color: winner === 'athlete2' ? winnerHighlightColor : athlete2Color,
                    minWidth: 'clamp(3rem, 6vw, 6rem)',
                    textAlign: 'left',
                    fontWeight: 'bold',
                    textShadow: winner === 'athlete2' ? `0 0 20px ${winnerHighlightColor}cc, 0 0 40px ${winnerHighlightColor}80` : undefined
                  }}
                >
                  {athlete2Total}
                </div>
              )}
            </div>

            {/* Athlete 2 Card */}
            {(() => {
              const cardBgS = ss(theme, 'battle_vote', 'card_background');
              const photoS = ss(theme, 'battle_vote', 'athlete_photo');
              const cardW = cardBgS.width ?? 1;
              const cardH = cardBgS.height ?? 1;
              return (
            <Card
              className={`athlete-card glass text-center transition-all duration-1000 ${
                winner === 'athlete1' ? 'opacity-70 scale-95' : ''
              }`}
              data-testid="athlete2-display"
              style={{
                border: winner === 'athlete2'
                  ? `clamp(3px, 0.5vw, 6px) solid ${winnerHighlightColor}`
                  : (cardBgS.border_color ? `1px solid ${cardBgS.border_color}` : 'none'),
                background: winner === 'athlete2' ? undefined : (cardBgS.bg_color || undefined),
                boxShadow: winner === 'athlete2' ? `0 0 50px ${winnerHighlightColor}e6, inset 0 0 30px ${winnerHighlightColor}33` : 'none',
                width: cardW !== 1 ? `${cardW * 100}%` : '100%',
                minHeight: cardH !== 1 ? `${cardH * 100}%` : undefined,
                // Card hugs the CENTER edge of its grid cell (left edge of the RIGHT cell) — mirror of athlete1.
                justifySelf: 'start',
                marginLeft: 0,
                transform: `translate(${(cardBgS.offset_x || 0)}px, ${cardBgS.offset_y || 0}px)`,
              }}
            >
              <div className="relative inline-block" style={{ marginBottom: '0.5vh', transform: `scale(${photoS.size}) ${offsetTransform(photoS)}`, transformOrigin: 'center' }}>
                {currentBattle.athlete2.photo_url ? (
                  <img
                    src={getImageUrl(currentBattle.athlete2.photo_url)}
                    alt={currentBattle.athlete2.name}
                    className="athlete-photo rounded-full mx-auto object-cover"
                    style={{
                      border: theme.photo_border_show !== false
                        ? `3px solid ${winner === 'athlete2' ? winnerHighlightColor : (photoS.border_color || theme.athlete_photo_border_color || '#ffffff')}`
                        : 'none'
                    }}
                  />
                ) : (
                  <div
                    className="athlete-photo rounded-full mx-auto flex items-center justify-center font-bold"
                    style={{
                      background: photoS.bg_color || `${athlete2Color}20`,
                      border: `3px solid ${winner === 'athlete2' ? winnerHighlightColor : (photoS.border_color || theme.athlete_photo_border_color || '#ffffff')}`,
                      color: photoS.color || athlete2Color,
                      fontSize: 'clamp(1.5rem, 3vw, 3rem)'
                    }}
                  >
                    {currentBattle.athlete2.name?.charAt(0)}
                  </div>
                )}
                <RoundFlag
                  countryName={currentBattle.athlete2.country}
                  configScreen="battle_vote"
                  configElement="athlete_flag"
                />
              </div>
              <div className="flex items-center justify-center gap-2" style={{ marginBottom: '0.25vh' }}>
                <h2
                  className="athlete-name font-bold uppercase leading-tight"
                  style={{
                    fontFamily: 'Montserrat, sans-serif',
                    color: ss(theme,'battle_vote','athlete_name',{color:'white'}).color,
                    transform: offsetTransform(ss(theme,'battle_vote','athlete_name')),
                    fontSize: `calc(1em * ${ss(theme,'battle_vote','athlete_name').size})`,
                    textShadow: ss(theme,'battle_vote','athlete_name').textShadow,
                  }}
                >
                  {currentBattle.athlete2.name}
                </h2>
                <span
                  className="athlete-ranking font-bold"
                  style={{
                    color: ss(theme,'battle_vote','athlete_wr',{color: theme.display_wr_color || '#fbbf24'}).color,
                    transform: offsetTransform(ss(theme,'battle_vote','athlete_wr')),
                    fontSize: `calc(1em * ${ss(theme,'battle_vote','athlete_wr').size})`,
                    textShadow: ss(theme,'battle_vote','athlete_wr').textShadow,
                  }}
                >
                  {currentBattle.athlete2.world_ranking ? `#${currentBattle.athlete2.world_ranking}` : 'N/A'}
                </span>
              </div>
              {winner === 'athlete2' && winnerType === 'judge_count' && null /* moved out of the card — see the absolute overlay below the grid */}
            </Card>
              );
            })()}
          </div>
          {/* Winner-by-judge-count label — positioned freely so it never gets clipped by the athlete cards.
              Its position is centered under the VS block by default, and every offset comes from the schema element `winner_by_judge_label`. */}
          {winnerType === 'judge_count' && (() => {
            const wbjS = ss(theme, 'battle_vote', 'winner_by_judge_label');
            return (
              <div
                data-testid="winner-by-judge-label"
                className="absolute font-bold uppercase whitespace-nowrap pointer-events-none"
                style={{
                  left: '50%',
                  top: '50%',
                  color: wbjS.color || '#ffffff',
                  transform: `translate(-50%, -50%) translate(${wbjS.offset_x || 0}px, ${wbjS.offset_y || 0}px) scale(${wbjS.size || 1})`,
                  fontSize: 'clamp(0.75rem, 1.4vw, 1.4rem)',
                  letterSpacing: '0.08em',
                  textShadow: wbjS.textShadow,
                  zIndex: 20,
                }}
              >
                {t('battle.winnerByJudgeCount')}
              </div>
            );
          })()}
          
          {/* Scores on mobile (below cards) */}
          <div className="md:hidden flex items-center justify-center" style={{ gap: 'clamp(0.5rem, 2vw, 2rem)', marginTop: '1vh' }}>
            {revealedVotes.length > 0 && (
              <>
                <div 
                  className={winner === 'athlete1' ? 'score-large' : 'score-small'}
                  style={{ 
                    color: winner === 'athlete1' ? winnerHighlightColor : athlete1Color,
                    fontWeight: 'bold',
                    textShadow: winner === 'athlete1' ? `0 0 15px ${winnerHighlightColor}cc` : undefined
                  }}
                >
                  {athlete1Total}
                </div>
                <span className="vs-text font-bold text-gray-400">VS</span>
                <div 
                  className={winner === 'athlete2' ? 'score-large' : 'score-small'}
                  style={{ 
                    color: winner === 'athlete2' ? winnerHighlightColor : athlete2Color,
                    fontWeight: 'bold',
                    textShadow: winner === 'athlete2' ? `0 0 15px ${winnerHighlightColor}cc` : undefined
                  }}
                >
                  {athlete2Total}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Judges Status */}
        <div className="flex justify-center flex-shrink-0">
          {(() => {
            const panelS = ss(theme, 'battle_vote', 'judges_panel_bg');
            const pW = panelS.width ?? 1;
            const pH = panelS.height ?? 1;
            return (
          <div
            className="rounded-lg"
            style={{
              padding: 'clamp(1rem, 3vw, 70px)',
              width: pW !== 1 ? `${pW * 100}%` : 'fit-content',
              minHeight: pH !== 1 ? `${pH * 100}%` : undefined,
              background: panelS.bg_color || 'rgba(0,0,0,0.45)',
              border: `1px solid ${panelS.border_color || 'rgba(255,255,255,0.1)'}`,
              backdropFilter: 'blur(10px)',
              transform: offsetTransform(panelS),
            }}
          >
            {(() => {
              // Prefer the judges array from /api/battle/current (authoritative
              // for this specific battle — makes 1v1 battles render exactly
              // the tournament's judges_count, no more phantom slots).
              const battleJudges = Array.isArray(currentBattle.judges) ? currentBattle.judges : null;
              const battleTournament = tournaments.find(t => t.id === currentBattle.battle?.tournament_id);
              const tournamentJudgesCount = battleTournament?.judges_count || 5;
              const _rawAssigned = battleJudges
                ? battleJudges.map(j => j.judge_number)
                : ((battleTournament?.assigned_judge_numbers && battleTournament.assigned_judge_numbers.length > 0)
                    ? battleTournament.assigned_judge_numbers
                    : Array.from({length: tournamentJudgesCount}, (_, i) => i + 1));
              const assignedJudges = sortJudgeNumbers(_rawAssigned, judges);
              const judgesCount = assignedJudges.length;
              
              const judgeGapMultiplier = (theme.display_judge_gap || 100) / 100;
              const baseGap = 'clamp(0.5rem, 1vw, 1.5rem)';
              
              return (
                <div
                  className="grid"
                  style={{
                    gap: `calc(${baseGap} * ${judgeGapMultiplier})`,
                    gridTemplateColumns: `repeat(${Math.max(1, judgesCount)}, minmax(0, 1fr))`,
                  }}
                >
                  {assignedJudges.map((judgeNum) => {
                  const vote = currentBattle.votes?.find(v => v.judge_number === judgeNum);
                  const revealed = vote?.revealed;
                  const hasVoted = !!vote;
                  const judge = judges.find(j => j.judge_number === judgeNum);
                  
                  // Highlight judge if they voted for winner in tie situation
                  const isWinningJudge = winnerType === 'judge_count' && vote && (
                    (winner === 'athlete1' && vote.athlete1_score > vote.athlete2_score) ||
                    (winner === 'athlete2' && vote.athlete2_score > vote.athlete1_score)
                  );

                  return (
                    <div 
                      key={judgeNum} 
                      className={`judge-card text-center ${isWinningJudge ? 'rounded-lg' : ''}`}
                      data-testid={`judge-status-${judgeNum}`}
                      style={{
                        background: isWinningJudge ? `${winnerHighlightColor}33` : 'transparent',
                        border: isWinningJudge ? `clamp(1px, 0.2vw, 2px) solid ${winnerHighlightColor}` : 'none',
                        boxShadow: isWinningJudge ? `0 0 20px ${winnerHighlightColor}66` : 'none',
                        animation: isWinningJudge ? 'pulse 2s ease-in-out infinite' : 'none',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center'
                      }}
                    >
                  <div style={{ marginBottom: '0.5vh' }}>
                    {(() => {
                      const dotS = ss(theme, 'battle_vote', 'judge_dot');
                      const dotStyle = { transform: `scale(${dotS.size}) ${offsetTransform(dotS)}`, transformOrigin: 'center' };
                      if (revealed) return <CheckCircle className="judge-icon mx-auto" style={{ color: dotS.color || theme.judge_revealed_color, ...dotStyle }} />;
                      if (hasVoted) return <CheckCircle className="judge-icon mx-auto" style={{ color: dotS.color || theme.judge_voted_color, ...dotStyle }} />;
                      return <Circle className="judge-icon mx-auto" style={{ color: dotS.color || theme.judge_waiting_color, ...dotStyle }} />;
                    })()}
                  </div>
                  {(() => {
                    const nameS = ss(theme, 'battle_vote', 'judge_name');
                    return (
                      <h4
                        className="judge-name font-bold uppercase"
                        style={{
                          color: nameS.color || 'white',
                          marginBottom: '0.25vh',
                          fontSize: `calc(1em * ${nameS.size})`,
                          transform: offsetTransform(nameS),
                          textShadow: nameS.textShadow,
                        }}
                      >
                        {judge?.first_name || `Judge ${judgeNum}`}
                      </h4>
                    );
                  })()}
                  {!revealed && (() => {
                    const stS = ss(theme, 'battle_vote', 'judge_status');
                    return (
                      <p
                        className="judge-status"
                        style={{
                          color: stS.color || theme.judge_status_text,
                          fontSize: `calc(1em * ${stS.size})`,
                          transform: offsetTransform(stS),
                          textShadow: stS.textShadow,
                        }}
                      >
                        {hasVoted ? t('display.voted') : t('display.waiting')}
                      </p>
                    );
                  })()}
                  <div style={{ minHeight: revealed ? 'clamp(3rem, 6vh, 4rem)' : 'clamp(2rem, 4vh, 3rem)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {revealed && vote && (
                      <div className="flex justify-center items-center" style={{ gap: 'clamp(0.25rem, 0.5vw, 0.5rem)', marginTop: '0.25vh' }}>
                        <div 
                          className="judge-score font-bold"
                          style={{ 
                            color: vote.athlete1_score > vote.athlete2_score ? winnerHighlightColor : athlete1Color,
                            opacity: 1,
                            fontSize: vote.athlete1_score > vote.athlete2_score ? 'clamp(1.5rem, 3vw, 4rem)' : 'clamp(1rem, 2vw, 3rem)',
                            animation: vote.athlete1_score > vote.athlete2_score ? 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' : 'none',
                            transform: vote.athlete1_score > vote.athlete2_score ? 'scale(1.05)' : 'scale(1)'
                          }}
                        >
                          {vote.athlete1_score}
                        </div>
                        <span className="text-gray-500" style={{ fontSize: 'clamp(0.75rem, 1.25vw, 1.25rem)' }}>-</span>
                        <div 
                          className="judge-score font-bold"
                          style={{ 
                            color: vote.athlete2_score > vote.athlete1_score ? winnerHighlightColor : athlete2Color,
                            opacity: 1,
                            fontSize: vote.athlete2_score > vote.athlete1_score ? 'clamp(1.5rem, 3vw, 4rem)' : 'clamp(1rem, 2vw, 3rem)',
                            animation: vote.athlete2_score > vote.athlete1_score ? 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' : 'none',
                            transform: vote.athlete2_score > vote.athlete1_score ? 'scale(1.05)' : 'scale(1)'
                          }}
                        >
                          {vote.athlete2_score}
                        </div>
                      </div>
                    )}
                    </div>
                  </div>
                );
              })}
                </div>
              );
            })()}
          </div>
              );
            })()}
        </div>
      </div>
        );
      })()}

      </div>{/* /display-canvas */}
      <ReloadButton />
    </div>
  );
};

export default DisplayPage;
